import { useEffect, useRef, useState } from 'react';
import type { ResolvedSchemaTree } from '@root/helpers/schema/types';
import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { IGNORE_IDS } from '../../constants';

interface DatapointAccordionProps {
  tree: ResolvedSchemaTree[];
  search: string;
  selectedId: string;
  onSelect: (id: string, label: string) => void;
}

function matchesSearch(node: ResolvedSchemaTree, term: string): boolean {
  if (node.title?.toLowerCase().includes(term)) return true;
  return node.children?.some((child) => matchesSearch(child as ResolvedSchemaTree, term)) ?? false;
}

function isLeaf(node: ResolvedSchemaTree): boolean {
  return !node.children?.length;
}

interface TreeNodeProps {
  node: ResolvedSchemaTree;
  depth: number;
  term: string;
  selectedId: string;
  onSelect: (id: string, label: string) => void;
}

function TreeNode({ node, depth, term, selectedId, onSelect }: TreeNodeProps) {
  const [expanded, setExpanded] = useState(selectedId.startsWith(node.id));

  if (term && !matchesSearch(node, term)) return null;

  if (isLeaf(node)) {
    return (
      <button
        id={node.id}
        type="button"
        data-testid="datapoint-field-option"
        className={clsx(
          'flex w-full border-b border-gray-100 px-2 py-2 text-left text-sm whitespace-nowrap last:border-0 hover:bg-gray-100',
          selectedId === node.id && 'bg-blue-50 font-medium text-blue-700',
        )}
        style={{ paddingLeft: `${(depth + 1) * 8}px` }}
        onClick={() => onSelect(node.id, node.title)}
      >
        {node.title}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={clsx(
          'flex w-full items-center gap-1 border-b border-gray-100 bg-gray-50 px-2 py-2 text-left text-sm tracking-wide text-gray-900 hover:bg-gray-100',
          'font-medium',
        )}
        style={{ paddingLeft: `${depth * 8 + 8}px` }}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? <ChevronDown className="size-3 shrink-0" /> : <ChevronRight className="size-3 shrink-0" />}
        {node.title}
      </button>
      {(expanded || !!term) &&
        node.children
          ?.filter((child) => !IGNORE_IDS.includes(child.id))
          .map((child, i) => (
            <TreeNode
              key={child.id ?? i}
              node={child as ResolvedSchemaTree}
              depth={depth + 1}
              term={term}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
    </>
  );
}

export function DatapointAccordion({ tree, search, selectedId, onSelect }: DatapointAccordionProps) {
  const term = search.toLowerCase();
  const hasVisible = !term || tree.some((node) => matchesSearch(node, term));

  const initialSelectedId = useRef(selectedId);
  useEffect(() => {
    const id = initialSelectedId.current;
    if (!id) return;
    const rafId = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  if (!hasVisible) return null;

  return (
    <div data-testid="datapoint-accordion">
      {tree.map((node, i) => (
        <TreeNode key={node.id ?? i} node={node} depth={0} term={term} selectedId={selectedId} onSelect={onSelect} />
      ))}
    </div>
  );
}
